import { useApiQuery } from "@/hooks/useApiQuery";
import { useTranslation } from "@/hooks/useTranslation";
import { ChatComposerExample } from "@shaarp/common";
import { useEffect, useMemo, useState } from "react";

/**
 * How long an example stays on screen, its slide in and out included. Also
 * drives the CSS cycle, so each example fades out exactly as the next mounts.
 */
const ROTATION_MS = 5000;

const shuffle = (values: readonly string[]): string[] => {
  const shuffled = [...values];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Example requests scrolling through the composer's placeholder while the field
 * is empty and nothing else claims the slot. They span everything the assistant
 * can be asked — prospecting, contacts, web, writing, meeting prep — so the
 * breadth shows up without the user having to guess it.
 *
 * The examples are written for this user from the team and user context, drawn
 * from a pre-generated pool, so the first one is on screen in a Redis read.
 * Until they land — and if the call fails — a translated set stands in: this
 * slot is a hint, never worth an empty composer or an error.
 *
 * Rendered as an overlay rather than a native `placeholder`, which cannot be
 * animated: the caller drops the attribute while this is on screen.
 */
export function ChatPlaceholderExamples() {
  const { t, currentLanguage } = useTranslation();
  const [index, setIndex] = useState(0);

  // Generated in the user's language, hence the key: switching language has to
  // ask for the set again rather than keep the previous one on screen.
  //
  // Never refetched once held: the field is emptied and retyped constantly, and
  // each of those remounts would otherwise cost a request for a hint the user
  // has already read.
  const { data: generated } = useApiQuery<ChatComposerExample[]>({
    key: ["chat-composer-examples", currentLanguage],
    path: "/my/team/ai-suggestions/chat-examples",
    staleTime: Infinity,
  });

  // Drawn when the screen opens, and again only if the language changes: two
  // consecutive chats do not open on the same example, and the order stays put
  // while the user reads.
  const fallback = useMemo(() => {
    const values = t("chat.input.examples", { returnObjects: true });
    return Array.isArray(values) ? shuffle(values as string[]) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  // The server already deals its pool across capabilities and puts the most
  // relevant first, so its order is kept as is.
  const examples = generated?.length
    ? generated.map((example) => example.text)
    : fallback;

  useEffect(() => {
    if (examples.length < 2) return;
    const interval = setInterval(
      () => setIndex((current) => (current + 1) % examples.length),
      ROTATION_MS,
    );
    return () => clearInterval(interval);
  }, [examples.length]);

  // Modulo rather than a plain lookup: the generated set replaces the stand-in
  // mid-rotation, and it may be the shorter of the two.
  const example = examples.length ? examples[index % examples.length] : null;
  if (!example) return null;

  // text-left: the new-chat screen centers its copy, and the examples belong
  // where the typed text will land, not under the heading.
  return (
    <div
      className="overflow-hidden absolute top-3 h-6 text-left select-none pointer-events-none inset-x-3"
      aria-hidden="true"
    >
      {/* Keyed on both so each example mounts fresh and replays the cycle: on
          the index, which every rotation changes, and on the text, so the
          handover from the stand-in to the generated set replays too. */}
      <span
        key={`${index}-${example}`}
        className="block text-base leading-6 truncate text-muted-foreground composer-example-cycle"
        style={{ animationDuration: `${ROTATION_MS}ms` }}
      >
        {example}
      </span>
    </div>
  );
}
